import React from 'react';
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {Sparkles, Type, X} from 'lucide-react';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';
import {InputModeToggle} from '../components/app/InputModeToggle';
import {InputPromptCard} from '../components/app/InputPromptCard';
import {CreateTimerButton} from '../components/app/CreateTimerButton';

export type BeforeAfterProps = {
  hook: string;
  beforeLabel: string;
  afterLabel: string;
  manualSteps: string[];
  cta: string;
};

const HOOK_DURATION = 36;
const SPLIT_START = 30;
const STEP_INTERVAL = 12;

export const BeforeAfter: React.FC<BeforeAfterProps> = ({
  hook,
  beforeLabel,
  afterLabel,
  manualSteps,
  cta,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const hookOpacity = interpolate(
    frame,
    [0, 8, HOOK_DURATION - 10, HOOK_DURATION],
    [0, 1, 1, 0.55],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const hookY = interpolate(frame, [0, 10], [40, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const splitProgress = spring({
    frame: Math.max(0, frame - SPLIT_START),
    fps,
    from: 0,
    to: 1,
    config: {damping: 18, stiffness: 80},
  });

  const beforeCardSpring = spring({
    frame: Math.max(0, frame - SPLIT_START - 4),
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 90},
  });
  const beforeOpacity = interpolate(frame, [SPLIT_START, SPLIT_START + 18], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const beforeY = interpolate(beforeCardSpring, [0, 1], [60, 0]);

  const afterCardSpring = spring({
    frame: Math.max(0, frame - SPLIT_START - 14),
    fps,
    from: 0,
    to: 1,
    config: {damping: 14, stiffness: 90},
  });
  const afterStartFrame = SPLIT_START + 14 + manualSteps.length * STEP_INTERVAL;
  const afterOpacity = interpolate(
    frame,
    [afterStartFrame, afterStartFrame + 18],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );
  const afterY = interpolate(afterCardSpring, [0, 1], [60, 0]);

  const ctaOpacity = interpolate(
    frame,
    [afterStartFrame + 30, afterStartFrame + 48],
    [0, 1],
    {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
  );

  return (
    <AbsoluteFill>
      <BgGradient />
      <Audio src={staticFile('music/before-after.mp3')} volume={0.4} />

      <AbsoluteFill
        style={{
          padding: '140px 60px 100px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 60,
        }}
      >
        <div
          style={{
            fontFamily: brandFont,
            fontSize: 82,
            fontWeight: 800,
            color: brand.textPrimary,
            textAlign: 'center',
            letterSpacing: -2,
            lineHeight: 1.02,
            whiteSpace: 'pre-line',
            opacity: hookOpacity,
            transform: `translateY(${hookY}px)`,
            textShadow: '0 12px 40px rgba(0,0,0,0.55)',
          }}
        >
          {hook}
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 36,
            width: '100%',
            opacity: splitProgress,
          }}
        >
          <BeforeCard
            label={beforeLabel}
            steps={manualSteps}
            opacity={beforeOpacity}
            y={beforeY}
            frame={frame}
            startFrame={SPLIT_START + 14}
          />

          <AfterCard
            label={afterLabel}
            opacity={afterOpacity}
            y={afterY}
          />

          <Img
            src={staticFile('logo.png')}
            style={{
              opacity: ctaOpacity,
              width: 220,
              height: 220,
              borderRadius: 48,
              boxShadow: `0 22px 70px ${brand.primary}55`,
              marginTop: 'auto',
              alignSelf: 'center',
            }}
          />
        </div>

        <div
          style={{
            opacity: ctaOpacity,
            fontFamily: brandFont,
            fontSize: 40,
            fontWeight: 700,
            color: brand.textPrimary,
            textAlign: 'center',
            letterSpacing: -0.5,
          }}
        >
          {cta}
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const BeforeCard: React.FC<{
  label: string;
  steps: string[];
  opacity: number;
  y: number;
  frame: number;
  startFrame: number;
}> = ({label, steps, opacity, y, frame, startFrame}) => (
  <div
    style={{
      opacity,
      transform: `translateY(${y}px)`,
      borderRadius: 32,
      padding: 32,
      background: 'rgba(255,80,80,0.08)',
      border: `1.5px solid ${brand.error}55`,
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    }}
  >
    <div style={{display: 'flex', alignItems: 'center', gap: 14}}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: `${brand.error}22`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Type size={30} color={brand.error} strokeWidth={2.4} />
      </div>
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 28,
          fontWeight: 800,
          color: brand.error,
          letterSpacing: 3,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
    </div>
    <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
      {steps.map((step, i) => {
        const appear = interpolate(
          frame,
          [startFrame + i * STEP_INTERVAL, startFrame + i * STEP_INTERVAL + 10],
          [0, 1],
          {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'},
        );
        const xShift = interpolate(appear, [0, 1], [-20, 0]);
        return (
          <div
            key={i}
            style={{
              opacity: appear,
              transform: `translateX(${xShift}px)`,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              fontFamily: brandFont,
              fontSize: 26,
              fontWeight: 500,
              color: brand.textSecondary,
              textDecoration: 'line-through',
              textDecorationColor: `${brand.error}aa`,
              textDecorationThickness: 3,
            }}
          >
            <X size={22} color={brand.error} strokeWidth={3} />
            {step}
          </div>
        );
      })}
    </div>
  </div>
);

const AfterCard: React.FC<{
  label: string;
  opacity: number;
  y: number;
}> = ({label, opacity, y}) => (
  <div
    style={{
      opacity,
      transform: `translateY(${y}px)`,
      borderRadius: 32,
      padding: 32,
      background: `linear-gradient(135deg, ${brand.primary}22 0%, ${brand.secondary}22 100%)`,
      border: `1.5px solid ${brand.primary}66`,
      boxShadow: `0 20px 60px ${brand.primary}33`,
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    }}
  >
    <div style={{display: 'flex', alignItems: 'center', gap: 14}}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 20px ${brand.primary}aa`,
        }}
      >
        <Sparkles size={30} color={brand.textPrimary} strokeWidth={2.4} />
      </div>
      <div
        style={{
          fontFamily: brandFont,
          fontSize: 28,
          fontWeight: 800,
          color: brand.primaryLight,
          letterSpacing: 3,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
    </div>

    <InputModeToggle selected="write" />
    <InputPromptCard />
    <CreateTimerButton isCreating={false} frame={0} />

    <div
      style={{
        fontFamily: brandFont,
        fontSize: 24,
        fontWeight: 700,
        color: brand.success,
        letterSpacing: 0.5,
      }}
    >
      Timer ready in 3 seconds.
    </div>
  </div>
);
